import React from 'react';
import { useIntl } from 'react-intl';
import { faUserMinus } from '@fortawesome/pro-light-svg-icons/faUserMinus';
import { TypedMemo } from '../common/types';
import { TeamConfirmModal } from './TeamConfirmModal';
import { emailInitials } from './emailInitials';
import styles from './TeamRemoveMemberModal.module.scss';

interface TeamRemoveMemberModalProps {
  /** The member to remove, or `null` when the modal is closed. */
  member: { email: string; joinedAt: Date } | null;
  isRemoving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Design p1: the owner confirms that a member leaves the workspace. */
const TeamRemoveMemberModalComponent: React.FC<TeamRemoveMemberModalProps> = ({
  member,
  isRemoving,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl();

  return (
    <TeamConfirmModal
      isOpen={member !== null}
      isTitleMasked
      icon={faUserMinus}
      title={intl.formatMessage(
        { id: 'app.sml.team.remove.title', defaultMessage: 'Remove {email} from the team?' },
        { email: member?.email ?? '' }
      )}
      confirmLabel={intl.formatMessage({ id: 'app.sml.team.remove.confirmButton', defaultMessage: 'Remove' })}
      isPending={isRemoving}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {member !== null && (
        <div className={`d-flex align-items-center gap-3 px-3 py-2 ${styles.who}`}>
          <span className={styles.avatar} aria-hidden="true">
            {emailInitials(member.email)}
          </span>
          <div className="text-break" data-clarity-mask="true">
            <b>{member.email}</b>
            <small className={`d-block ${styles.whoMeta}`}>
              {intl.formatMessage(
                { id: 'app.sml.team.remove.memberJoined', defaultMessage: 'Member · joined {date}' },
                { date: intl.formatDate(member.joinedAt, { day: 'numeric', month: 'short' }) }
              )}
            </small>
          </div>
        </div>
      )}
      <p className="mb-0">
        {intl.formatMessage({
          id: 'app.sml.team.remove.body',
          defaultMessage: 'They lose access to this workspace right away. Their seat is free again.',
        })}
      </p>
    </TeamConfirmModal>
  );
};

export const TeamRemoveMemberModal = TypedMemo(TeamRemoveMemberModalComponent);
